const BUSINESS_TYPES = Object.freeze({
  SOLE_TRADER: 'sole_trader',
  LIMITED_COMPANY: 'limited_company'
});

const EXPENSE_TYPES = new Set(['expense', 'expenses']);

export const ADVISORY_SOURCES = {
  revenueVatReclaim: {
    label: 'Revenue VAT reclaim rules',
    url: 'https://www.revenue.ie/en/vat/reclaiming-vat/who-can-reclaim-vat.aspx'
  },
  revenueVatThresholds: {
    label: 'Revenue VAT registration thresholds',
    url: 'https://www.revenue.ie/en/vat/vat-registration/who-should-register-for-vat/vat-thresholds.aspx'
  },
  revenueVatRates: {
    label: 'Revenue current VAT rates',
    url: 'https://www.revenue.ie/en/vat/vat-rates/search-vat-rates/current-VAT-rates.aspx'
  },
  revenueBeautySalonVat: {
    label: 'Revenue beauty salon VAT rate',
    url: 'https://www.revenue.ie/en/vat/vat-rates/search-vat-rates/b/beauty-salon-miscellaneous-services-.aspx'
  },
  revenueExpenses: {
    label: 'Revenue expense deduction guidance',
    url: 'https://www.revenue.ie/en/starting-a-business/claiming-a-deduction-for-expenses/index.aspx'
  },
  revenueEmployeeExpenses: {
    label: 'Revenue employee expenses guidance',
    url: 'https://www.revenue.ie/en/employing-people/employee-expenses/employee-expenses-overview/index.aspx'
  },
  revenueCorporationTax: {
    label: 'Revenue corporation tax rates',
    url: 'https://www.revenue.ie/en/companies-and-charities/corporation-tax-for-companies/corporation-tax/basis-of-charge.aspx'
  },
  dspPrsiClassA: {
    label: 'Department of Social Protection PRSI Class A rates',
    url: 'https://www.gov.ie/en/department-of-social-protection/publications/prsi-class-a-rates/'
  },
  dspDirectorPrsi: {
    label: 'Department of Social Protection director PRSI guidance',
    url: 'https://www.gov.ie/en/department-of-social-protection/publications/prsi-and-family-employment/'
  },
  salonCostBenchmarks: {
    label: 'Salon cost benchmark guide',
    url: 'https://www.joinhomebase.com/blog/salon-monthly-expenses'
  },
  salonPricingGuide: {
    label: 'Beauty business pricing guide',
    url: 'https://beancount.io/blog/2026/03/14/beauty-salon-financial-management-complete-guide'
  }
};

const TAX_SENSITIVE_EXPENSE_CATEGORIES = new Set([
  'meals',
  'food',
  'food_and_drink',
  'entertainment',
  'vehicle_fuel',
  'vehicle',
  'travel_expenses'
]);

const VAT_BLOCKED_IRELAND_CATEGORIES = new Set([
  'meals',
  'food',
  'food_and_drink',
  'entertainment',
  'vehicle_fuel',
  'vehicle'
]);

const GENERAL_EXPENSE_CATEGORY_IDS = new Set(['misc', 'other', 'general_expenses', 'uncategorized', '']);

const BEAUTY_SERVICE_INCOME_CATEGORIES = new Set([
  'facial',
  'injectables',
  'body_treatments',
  'massage',
  'hair',
  'nails',
  'brows',
  'lashes',
  'makeup',
  'consulting',
  'training',
  'hairservices',
  'nailservices',
  'skinservices',
  'browservices',
  'lashservices',
  'makeupservices',
  'massageservices'
]);

const RETAIL_INCOME_CATEGORIES = new Set(['retail', 'product_sales', 'productsales', 'products', 'retail_products']);
const GIFT_CARD_INCOME_CATEGORIES = new Set(['gift_card', 'giftcard', 'gift_voucher', 'giftvoucher', 'voucher']);
const RETAIL_STOCK_EXPENSE_CATEGORIES = new Set(['retail_stock']);
const SUPPLY_EXPENSE_CATEGORIES = new Set(['supplies', 'materials', 'salon_tools']);
const UNIFORM_EXPENSE_CATEGORIES = new Set(['uniform_costs', 'clothing']);
const TRAINING_EXPENSE_CATEGORIES = new Set(['training', 'training_materials', 'education']);
const MARKETING_EXPENSE_CATEGORIES = new Set(['marketing', 'advertising']);
const SOFTWARE_EXPENSE_CATEGORIES = new Set(['software']);
const TIPS_EXPENSE_CATEGORIES = new Set(['tips', 'tip', 'gratuity', 'gratuities']);
const CAPITAL_EXPENSE_CATEGORIES = new Set(['equipment', 'furniture', 'electronics', 'vehicle', 'salon_tools']);
const MIXED_USE_EXPENSE_CATEGORIES = new Set([
  'internet_telephone',
  'utilities',
  'rent',
  'vehicle',
  'vehicle_fuel',
  'vehicle_repairs_licensing',
  'travel_expenses'
]);

const normalizeString = (value) => String(value || '').trim().toLowerCase();

const normalizeCountryId = (currentCountry = {}) => {
  const id = normalizeString(currentCountry.id);
  const name = normalizeString(currentCountry.name);
  if (id === 'ie' || name.includes('ireland')) return 'ie';
  if (id === 'uk' || id === 'gb' || name.includes('united kingdom')) return 'uk';
  if (id === 'us' || name.includes('united states')) return 'us';
  return id || 'ie';
};

const normalizeBusinessType = (value) => (
  value === BUSINESS_TYPES.LIMITED_COMPANY
    ? BUSINESS_TYPES.LIMITED_COMPANY
    : BUSINESS_TYPES.SOLE_TRADER
);

const getTransactionType = (transaction = {}) => normalizeString(transaction.type);

const getTransactionAmount = (transaction = {}) => {
  const amount = Number(transaction.amount);
  return Number.isFinite(amount) ? amount : 0;
};

const getTransactionCategory = (transaction = {}) => normalizeString(transaction.category);

const isExpenseTransaction = (transaction = {}) => EXPENSE_TYPES.has(getTransactionType(transaction));

const isIncomeTransaction = (transaction = {}) => getTransactionType(transaction) === 'income';

const isDateInCurrentMonth = (value, referenceDate) => {
  if (!value) return false;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  return date.getFullYear() === referenceDate.getFullYear() &&
    date.getMonth() === referenceDate.getMonth();
};

const sumByCategory = (transactions, typePredicate) => {
  const totals = {};
  const counts = {};

  transactions
    .filter(typePredicate)
    .forEach((transaction) => {
      const category = getTransactionCategory(transaction);
      totals[category] = (totals[category] || 0) + getTransactionAmount(transaction);
      counts[category] = (counts[category] || 0) + 1;
    });

  return { totals, counts };
};

const includesAnyCategory = (categoryCounts, categorySet) => (
  Object.keys(categoryCounts || {}).some((category) => categorySet.has(category))
);

const countMatchingCategories = (categoryCounts, categorySet) => (
  Object.entries(categoryCounts || {}).reduce((sum, [category, count]) => (
    categorySet.has(category) ? sum + count : sum
  ), 0)
);

const sourceList = (sourceIds = []) => sourceIds
  .map((id) => ADVISORY_SOURCES[id])
  .filter(Boolean);

const createCard = ({
  id,
  title,
  summary,
  detail = '',
  category = 'bookkeeping',
  priority = 0,
  severity = 'info',
  actions = [],
  sourceIds = []
}) => ({
  id,
  title,
  summary,
  detail,
  category,
  priority,
  severity,
  actions,
  sources: sourceList(sourceIds)
});

export const getIrelandEmployerPrsiRates = (referenceDateInput = new Date()) => {
  const referenceDate = referenceDateInput instanceof Date
    ? referenceDateInput
    : new Date(referenceDateInput);
  const effectiveFromOctober = !Number.isNaN(referenceDate.getTime()) &&
    referenceDate >= new Date(2026, 9, 1);

  return {
    thresholdWeekly: 552,
    lowerWeeklyBandLabel: '€38 to €552',
    higherWeeklyBandLabel: 'over €552',
    currentPeriodLabel: effectiveFromOctober
      ? 'from 1 October 2026'
      : 'up to 30 September 2026',
    lowerEmployerRate: effectiveFromOctober ? 9.15 : 9.0,
    higherEmployerRate: effectiveFromOctober ? 11.4 : 11.25,
    employeeRate: effectiveFromOctober ? 4.35 : 4.2,
    nextChange: effectiveFromOctober
      ? null
      : {
          dateLabel: '1 October 2026',
          lowerEmployerRate: 9.15,
          higherEmployerRate: 11.4,
          employeeRate: 4.35
        }
  };
};

export const getIrelandEmployerPrsiSummary = (referenceDateInput = new Date()) => {
  const rates = getIrelandEmployerPrsiRates(referenceDateInput);
  const current = `Ireland employer PRSI Class A ${rates.currentPeriodLabel}: ${rates.lowerEmployerRate.toFixed(2)}% on weekly pay ${rates.lowerWeeklyBandLabel}, and ${rates.higherEmployerRate.toFixed(2)}% on weekly pay ${rates.higherWeeklyBandLabel}.`;
  const future = rates.nextChange
    ? ` From ${rates.nextChange.dateLabel}, use ${rates.nextChange.lowerEmployerRate.toFixed(2)}% up to €552/week and ${rates.nextChange.higherEmployerRate.toFixed(2)}% over €552/week.`
    : '';
  return `${current}${future}`;
};

export const suggestIrelandEmployerPrsiLoading = (weeklyPay, referenceDateInput = new Date()) => {
  const pay = Number(weeklyPay);
  if (!Number.isFinite(pay) || pay < 38) return 0;
  const rates = getIrelandEmployerPrsiRates(referenceDateInput);
  return pay > rates.thresholdWeekly ? rates.higherEmployerRate : rates.lowerEmployerRate;
};

export const buildAiAdvisorStats = ({
  transactions = [],
  targetIncomeData = {},
  currentCountry = {},
  businessType = BUSINESS_TYPES.SOLE_TRADER,
  vatSettings = {},
  pricingLaborHourlyCost = null,
  referenceDate = new Date()
} = {}) => {
  const safeTransactions = Array.isArray(transactions) ? transactions : [];
  const safeTarget = targetIncomeData && typeof targetIncomeData === 'object' ? targetIncomeData : {};
  const countryId = normalizeCountryId(currentCountry);
  const normalizedBusinessType = normalizeBusinessType(businessType);
  const expenses = safeTransactions.filter(isExpenseTransaction);
  const income = safeTransactions.filter(isIncomeTransaction);
  const currentMonthIncome = income
    .filter((transaction) => isDateInCurrentMonth(transaction.date, referenceDate))
    .reduce((sum, transaction) => sum + getTransactionAmount(transaction), 0);
  const currentMonthExpenses = expenses
    .filter((transaction) => isDateInCurrentMonth(transaction.date, referenceDate))
    .reduce((sum, transaction) => sum + getTransactionAmount(transaction), 0);
  const expenseCategoryData = sumByCategory(safeTransactions, isExpenseTransaction);
  const incomeCategoryData = sumByCategory(safeTransactions, isIncomeTransaction);
  const labourProfiles = Array.isArray(safeTarget.labourProfiles) ? safeTarget.labourProfiles : [];
  const staffMembers = Array.isArray(safeTarget.staffMembers) ? safeTarget.staffMembers : [];
  const staffPlanSummary = safeTarget.staffPlanSummary && typeof safeTarget.staffPlanSummary === 'object'
    ? safeTarget.staffPlanSummary
    : {};
  const labourHourlyFromBudget = Number(pricingLaborHourlyCost ?? safeTarget.pricingLaborHourlyCost ?? safeTarget.goalHourly);
  const weeklyOpenHours = Number(safeTarget.totalWeeklyOpenHours || safeTarget.workHoursPerWeek || 0);
  const hasBudgetProfitGoal = Number(safeTarget.businessProfitGoalAmount || safeTarget.amount || 0) > 0;
  const hasLabourPlan = labourProfiles.some((profile) => Number(profile?.loadedHourlyCost) > 0);

  return {
    countryId,
    countryName: currentCountry.name || (countryId === 'ie' ? 'Ireland' : 'Selected country'),
    taxSystem: currentCountry.taxSystem || (countryId === 'us' ? 'sales' : 'vat'),
    businessType: normalizedBusinessType,
    isIreland: countryId === 'ie',
    isLimitedCompany: normalizedBusinessType === BUSINESS_TYPES.LIMITED_COMPANY,
    isSoleTrader: normalizedBusinessType === BUSINESS_TYPES.SOLE_TRADER,
    vatRegistered: Boolean(vatSettings?.enabled && vatSettings?.isRegistered),
    totalTransactions: safeTransactions.length,
    totalIncomeTransactions: income.length,
    totalExpenseTransactions: expenses.length,
    currentMonthIncome,
    currentMonthExpenses,
    currentMonthProfit: currentMonthIncome - currentMonthExpenses,
    expenseCategoryTotals: expenseCategoryData.totals,
    expenseCategoryCounts: expenseCategoryData.counts,
    incomeCategoryTotals: incomeCategoryData.totals,
    incomeCategoryCounts: incomeCategoryData.counts,
    hasTaxSensitiveExpenses: includesAnyCategory(expenseCategoryData.counts, TAX_SENSITIVE_EXPENSE_CATEGORIES),
    taxSensitiveExpenseCount: countMatchingCategories(expenseCategoryData.counts, TAX_SENSITIVE_EXPENSE_CATEGORIES),
    hasVatBlockedIrelandExpenses: includesAnyCategory(expenseCategoryData.counts, VAT_BLOCKED_IRELAND_CATEGORIES),
    vatBlockedIrelandExpenseCount: countMatchingCategories(expenseCategoryData.counts, VAT_BLOCKED_IRELAND_CATEGORIES),
    generalExpenseCount: countMatchingCategories(expenseCategoryData.counts, GENERAL_EXPENSE_CATEGORY_IDS),
    hasBeautyServiceIncome: countMatchingCategories(incomeCategoryData.counts, BEAUTY_SERVICE_INCOME_CATEGORIES) > 0,
    hasRetailIncome: countMatchingCategories(incomeCategoryData.counts, RETAIL_INCOME_CATEGORIES) > 0,
    hasGiftCardIncome: countMatchingCategories(incomeCategoryData.counts, GIFT_CARD_INCOME_CATEGORIES) > 0,
    hasRetailStockCosts: countMatchingCategories(expenseCategoryData.counts, RETAIL_STOCK_EXPENSE_CATEGORIES) > 0,
    hasSupplyCosts: countMatchingCategories(expenseCategoryData.counts, SUPPLY_EXPENSE_CATEGORIES) > 0,
    hasUniformCosts: countMatchingCategories(expenseCategoryData.counts, UNIFORM_EXPENSE_CATEGORIES) > 0,
    hasTrainingCosts: countMatchingCategories(expenseCategoryData.counts, TRAINING_EXPENSE_CATEGORIES) > 0,
    hasMarketingCosts: countMatchingCategories(expenseCategoryData.counts, MARKETING_EXPENSE_CATEGORIES) > 0,
    hasSoftwareCosts: countMatchingCategories(expenseCategoryData.counts, SOFTWARE_EXPENSE_CATEGORIES) > 0,
    hasTips: countMatchingCategories(expenseCategoryData.counts, TIPS_EXPENSE_CATEGORIES) > 0,
    hasCapitalPurchases: countMatchingCategories(expenseCategoryData.counts, CAPITAL_EXPENSE_CATEGORIES) > 0,
    hasMixedUseCosts: countMatchingCategories(expenseCategoryData.counts, MIXED_USE_EXPENSE_CATEGORIES) > 0,
    hasMeals: countMatchingCategories(expenseCategoryData.counts, new Set(['meals', 'food', 'food_and_drink'])) > 0,
    hasVehicleFuel: countMatchingCategories(expenseCategoryData.counts, new Set(['vehicle_fuel'])) > 0,
    hasLoanRepayments: countMatchingCategories(expenseCategoryData.counts, new Set(['loan_repayment'])) > 0,
    hasInterest: countMatchingCategories(expenseCategoryData.counts, new Set(['interest'])) > 0,
    hasBankCharges: countMatchingCategories(expenseCategoryData.counts, new Set(['bank_charges'])) > 0,
    hasDirectorLoan: countMatchingCategories(expenseCategoryData.counts, new Set(['director_loan'])) > 0 ||
      countMatchingCategories(incomeCategoryData.counts, new Set(['director_loan'])) > 0,
    hasStaffCosts: countMatchingCategories(expenseCategoryData.counts, new Set(['staffcosts', 'staffCosts'.toLowerCase(), 'employer_prsi'])) > 0,
    targetIncomeData: safeTarget,
    hasBudgetProfitGoal,
    weeklyOpenHours: Number.isFinite(weeklyOpenHours) ? weeklyOpenHours : 0,
    labourProfiles,
    staffMembers,
    staffPlanSummary,
    hasLabourPlan,
    hasStaffPlan: safeTarget.teamMode === 'staff' || staffMembers.length > 0,
    ownerRole: safeTarget.ownerRole || '',
    ownerPayMethod: safeTarget.ownerPayMethod || '',
    ownerInServiceTeam: ['service_team', 'occasional'].includes(safeTarget.ownerRole),
    labourHourlyFromBudget: Number.isFinite(labourHourlyFromBudget) ? labourHourlyFromBudget : 0
  };
};

export const buildAiAdviceCards = (input = {}) => {
  const stats = input.totalTransactions !== undefined
    ? input
    : buildAiAdvisorStats(input);
  const cards = [];
  const prsiSummary = getIrelandEmployerPrsiSummary(input.referenceDate || new Date());

  if (stats.totalTransactions === 0) {
    cards.push(createCard({
      id: 'start-with-clean-data',
      title: 'Start with clean income and expense tracking',
      summary: 'The assistant will become more specific once income, expenses, VAT settings, and budget data are entered.',
      detail: 'For now, focus on logging every sale and every business cost with the right category. This keeps tax estimates, pricing, and profit reports useful from day one.',
      category: 'setup',
      priority: 98,
      actions: ['Add every income source, including cash and card sales.', 'Attach or keep receipts for business purchases.', 'Avoid using General Expenses unless no category fits.']
    }));
  }

  if (stats.vatRegistered && stats.isIreland && stats.hasVatBlockedIrelandExpenses) {
    cards.push(createCard({
      id: 'ireland-vat-blocked-costs',
      title: 'Do not reclaim VAT on food, meals, takeaways, or petrol',
      summary: `${stats.vatBlockedIrelandExpenseCount} tracked expense(s) are in categories where Irish VAT should not be reclaimed.`,
      detail: 'VAT on food, meals, eating out, takeaways, and petrol should be treated as not reclaimable even when the business is VAT registered. Record the gross cost and keep the receipt, but do not mark the VAT as reclaimable.',
      category: 'vat',
      priority: 100,
      severity: 'warning',
      actions: ['Use Meals for food, eating out, and takeaway receipts.', 'Use Vehicle Fuel for petrol receipts.', 'Record these costs gross and leave VAT reclaim off.'],
      sourceIds: ['revenueVatReclaim']
    }));
  } else if (stats.vatRegistered && stats.isIreland) {
    cards.push(createCard({
      id: 'ireland-vat-reclaim-basics',
      title: 'VAT reclaim only works for business taxable supplies',
      summary: 'Being VAT registered does not mean every purchase has reclaimable VAT.',
      detail: 'For Ireland, VAT is generally reclaimable only where the purchase is used for taxable business supplies and you keep a valid VAT invoice. Do not reclaim VAT on food, meals, takeaways, or petrol.',
      category: 'vat',
      priority: 74,
      actions: ['Keep VAT invoices for reclaimable business purchases.', 'Split mixed business/private costs so only the business portion is claimed.', 'Record food, meals, takeaways, and petrol gross with no VAT reclaim.'],
      sourceIds: ['revenueVatReclaim']
    }));
  }

  if (!stats.vatRegistered && stats.taxSystem === 'vat') {
    cards.push(createCard({
      id: 'vat-not-registered',
      title: 'VAT is off for this business',
      summary: 'Do not treat purchase VAT as reclaimable until the business is VAT registered.',
      detail: 'You can still record the full expense for profit tracking, but VAT returns and VAT reclaim should stay off until registration is confirmed.',
      category: 'vat',
      priority: 72,
      actions: ['Record gross expense totals.', 'Turn VAT on only when registration is confirmed.', 'Check the VAT threshold with an accountant if sales are growing.']
    }));
  }

  if (stats.isIreland && !stats.vatRegistered) {
    cards.push(createCard({
      id: 'ireland-vat-thresholds-turnover',
      title: 'VAT threshold is based on turnover, not profit',
      summary: 'For Ireland, Revenue checks VAT registration thresholds against sales turnover over a continuous 12-month period.',
      detail: 'The principal thresholds are €42,500 for services only and €85,000 for goods. Mixed service and retail salons should monitor the split carefully because services and goods can point to different thresholds.',
      category: 'vat',
      priority: 69,
      actions: ['Track service sales and retail product sales separately.', 'Review rolling 12-month turnover, not yearly profit only.', 'Ask an accountant before delaying VAT registration when sales are close to a threshold.'],
      sourceIds: ['revenueVatThresholds']
    }));
  }

  if (stats.isIreland && (stats.vatRegistered || stats.hasBeautyServiceIncome || stats.hasRetailIncome)) {
    cards.push(createCard({
      id: 'ireland-beauty-salon-vat-rate',
      title: 'Check the right VAT rate for beauty services and retail',
      summary: 'Revenue lists beauty salon and beauty therapy services at the reduced VAT rate, currently 13.5%.',
      detail: 'The Revenue beauty salon entry says this does not include sales of cosmetics or sunbed treatments, which are liable at the standard rate. Retail product sales should be checked separately from service sales.',
      category: 'vat',
      priority: 67,
      actions: ['Use the reduced rate for qualifying beauty services.', 'Use the standard rate for cosmetics or sunbed treatments unless Revenue guidance says otherwise.', 'Separate retail sales from service income before filing VAT.'],
      sourceIds: ['revenueBeautySalonVat', 'revenueVatRates']
    }));
  }

  if (stats.hasRetailIncome || stats.hasRetailStockCosts) {
    cards.push(createCard({
      id: 'separate-services-retail-stock',
      title: 'Separate service sales from retail product sales',
      summary: 'Salon profit gets confusing when retail stock, back-bar product use, and service income are mixed together.',
      detail: 'Retail products bought for resale belong in Retail Products or stock tracking. Products used during treatments should feed the service product cost in Pricing Calculator so each treatment covers what it actually consumes.',
      category: 'bookkeeping',
      priority: 63,
      actions: ['Post retail product sales to Retail Products income.', 'Post products bought for resale to Retail Products expense.', 'Post treatment-use products to Supplies or the service cost calculator.'],
      sourceIds: ['salonPricingGuide']
    }));
  }

  if (stats.hasGiftCardIncome) {
    cards.push(createCard({
      id: 'gift-cards-deposits-not-normal-sales',
      title: 'Gift cards and deposits need separate tracking',
      summary: 'A gift card, voucher, package, or booking deposit can be money received before the final service is delivered.',
      detail: 'Keep these amounts separate so the same sale is not counted twice when the client redeems it. VAT timing can depend on registration status and how the sale is documented, so review these before filing.',
      category: 'bookkeeping',
      priority: 62,
      actions: ['Use Gift Card for voucher sales.', 'Do not record the full service sale again when a voucher is redeemed.', 'Reverse or adjust deposits when refunded or applied to an appointment.']
    }));
  }

  if (stats.hasUniformCosts) {
    cards.push(createCard({
      id: 'ordinary-clothing-warning',
      title: 'Do not treat ordinary clothing as a business expense',
      summary: 'Ordinary clothes are not normally claimable just because they are worn in the salon.',
      detail: 'Revenue lists clothing as an expense that cannot be claimed, except protective clothing. Use Uniform Costs only for salon-only uniforms, branded items, or protective wear that is genuinely business related.',
      category: 'tax',
      priority: 66,
      severity: 'warning',
      actions: ['Do not claim normal clothes, shoes, or outfits used outside work.', 'Keep invoices for branded or protective salon wear.', 'Ask an accountant before claiming dress-code clothing.'],
      sourceIds: ['revenueExpenses']
    }));
  }

  if (stats.hasTrainingCosts) {
    cards.push(createCard({
      id: 'training-costs-records',
      title: 'Keep training course records clear',
      summary: 'Beauty training can be a genuine business cost, but the business reason should be easy to prove.',
      detail: 'Use Courses & Learning for training that supports current salon services or required professional skills. New qualifications, personal development, and mixed-purpose courses may need accountant review.',
      category: 'bookkeeping',
      priority: 52,
      actions: ['Save the invoice, course outline, and payment proof.', 'Add a note explaining which service or business activity it supports.', 'Keep VAT reclaim off unless the business is VAT registered and the invoice supports reclaim.'],
      sourceIds: ['revenueExpenses']
    }));
  }

  if (stats.hasTips) {
    cards.push(createCard({
      id: 'tips-need-separate-tracking',
      title: 'Keep tips separate from service income',
      summary: 'Tips and gratuities should not distort treatment sales, labour cost, or profit margins.',
      detail: 'Track tips separately from service income and staff wages. If tips are passed to staff, keep a clear record of who received them and when.',
      category: 'bookkeeping',
      priority: 51,
      actions: ['Use Tips for gratuities.', 'Do not include tips when judging service price profitability.', 'Keep staff tip records separate from normal payroll.']
    }));
  }

  if (stats.hasMixedUseCosts) {
    cards.push(createCard({
      id: 'mixed-use-costs-apportionment',
      title: 'Split mixed business and personal costs',
      summary: 'Phone, utilities, rent, travel, and vehicle costs often include both business and private use.',
      detail: 'Revenue allows only the business portion of mixed-use costs. Keep a simple calculation showing the business percentage used in the ledger.',
      category: 'bookkeeping',
      priority: 50,
      actions: ['Add notes for business-use percentages.', 'Do not claim home-to-work travel as a business trip.', 'Review mixed-use costs before tax filing.'],
      sourceIds: ['revenueExpenses']
    }));
  }

  if (stats.hasCapitalPurchases) {
    cards.push(createCard({
      id: 'equipment-capital-purchases',
      title: 'Equipment may need capital treatment',
      summary: 'Salon beds, machines, furniture, electronics, and larger tools are not always ordinary day-to-day expenses.',
      detail: 'Large equipment and furniture can affect capital allowances or fixed asset tracking. Keep them separate from treatment product costs and general overheads.',
      category: 'tax',
      priority: 49,
      actions: ['Use Equipment, Furniture, Electronics, Vehicle, or Salon Tools as appropriate.', 'Keep purchase invoices and finance agreements.', 'Ask an accountant before treating large assets as normal expenses.'],
      sourceIds: ['revenueExpenses']
    }));
  }

  if (stats.generalExpenseCount >= 3) {
    cards.push(createCard({
      id: 'reduce-general-expenses',
      title: 'Too many broad expense categories can weaken tax reports',
      summary: `${stats.generalExpenseCount} expense(s) are sitting in broad categories such as General or Miscellaneous.`,
      detail: 'Specific categories make it easier to spot claimable costs, blocked VAT, payroll costs, bank charges, interest, and capital items.',
      category: 'bookkeeping',
      priority: 70,
      actions: ['Move bank fees to Bank Charges.', 'Move loan interest to Interest.', 'Move staff wages and employer costs to Staff Costs.']
    }));
  }

  if (stats.hasLoanRepayments && !stats.hasInterest) {
    cards.push(createCard({
      id: 'split-loan-repayment-interest',
      title: 'Split loan repayments between principal and interest',
      summary: 'Loan repayments include different parts for bookkeeping and tax.',
      detail: 'The capital/principal repayment is not usually a profit and loss expense, but business loan interest may be deductible. Track the interest part separately in the Interest category.',
      category: 'bookkeeping',
      priority: 78,
      severity: 'warning',
      actions: ['Use Loan Repayment for the principal amount.', 'Use Interest for the finance cost.', 'Keep loan statements as support.'],
      sourceIds: ['revenueExpenses']
    }));
  } else if (!stats.hasInterest && stats.totalExpenseTransactions > 0) {
    cards.push(createCard({
      id: 'use-interest-category',
      title: 'Use Interest for finance costs',
      summary: 'Interest is now its own expense category, which keeps finance costs out of general overheads.',
      detail: 'Revenue lists interest on money borrowed to finance the business as an example of a business expense. Principal repayments should stay separate from interest.',
      category: 'bookkeeping',
      priority: 45,
      actions: ['Post bank loan interest to Interest.', 'Keep loan repayments separate from interest.', 'Review bank statements monthly.'],
      sourceIds: ['revenueExpenses']
    }));
  }

  if (!stats.hasBankCharges && stats.totalExpenseTransactions > 0) {
    cards.push(createCard({
      id: 'use-bank-charges-category',
      title: 'Keep bank charges separate',
      summary: 'Bank fees and card processing charges should not disappear into General Expenses.',
      detail: 'Using the Bank Charges category makes overhead reports cleaner and helps pricing include the cost of getting paid.',
      category: 'bookkeeping',
      priority: 42,
      actions: ['Record monthly bank fees as Bank Charges.', 'Record payment processor fees separately where possible.']
    }));
  }

  if (stats.isLimitedCompany) {
    cards.push(createCard({
      id: 'limited-company-director-loan',
      title: "Use Director's Loan for owner money in or out",
      summary: "For a limited company, owner funding and repayments should not be mixed with normal sales or salon expenses.",
      detail: "Use Director's Loan when the director lends money to the company or the company repays the director. Payroll, dividends, and business expenses should stay in their own categories.",
      category: 'tax',
      priority: stats.hasDirectorLoan ? 55 : 82,
      actions: ["Use Director's Loan for director funding and repayments.", 'Use Staff Costs for payroll.', 'Use dividends/drawings notes outside normal sales income.']
    }));

    if (stats.countryId === 'ie') {
      cards.push(createCard({
        id: 'ireland-corporation-tax-profit',
        title: 'Company profit is not the same as owner pay',
        summary: 'Irish corporation tax is 12.5% on trading income, but payroll and dividends are separate owner-pay decisions.',
        detail: 'For limited companies, the app should focus on profit before corporation tax and profit after corporation tax. If the owner is paid through payroll, that cost reduces company profit before corporation tax.',
        category: 'tax',
        priority: 80,
        actions: ['Treat owner payroll as labour/payroll cost if the owner works in the business.', 'Treat dividends as an owner extraction decision, not service labour cost.', 'Use profit after corporation tax for retained company profit planning.'],
        sourceIds: ['revenueCorporationTax']
      }));
    }
  } else {
    cards.push(createCard({
      id: 'sole-trader-drawings',
      title: 'Sole trader drawings are not an expense',
      summary: 'Taking money out for yourself does not reduce taxable business profit.',
      detail: 'For a sole trader, the business profit after allowable expenses is what drives income tax planning. Owner drawings are cash movement, not a business cost.',
      category: 'tax',
      priority: 65,
      actions: ['Keep personal drawings separate from expenses.', 'Use the budget owner pay target for planning only.', 'Record actual business costs with receipts.'],
      sourceIds: ['revenueExpenses']
    }));
  }

  if (!stats.hasBudgetProfitGoal || stats.weeklyOpenHours <= 0) {
    cards.push(createCard({
      id: 'budget-profit-goal-clarity',
      title: 'Budget goal should be profit, not sales',
      summary: 'The budget planner asks for the profit the business should keep after costs, not total sales income.',
      detail: 'Sales targets belong in pricing and revenue planning. Budget profit goals should sit after labour, product costs, overheads, and tax planning.',
      category: 'budget',
      priority: 88,
      actions: ['Enter the desired business profit goal.', 'Enter open days and total weekly open hours.', 'Add owner or staff labour only if they deliver services.']
    }));
  }

  if (!stats.hasLabourPlan || stats.labourHourlyFromBudget <= 0) {
    cards.push(createCard({
      id: 'pricing-needs-loaded-labour',
      title: 'Pricing needs a loaded labour cost',
      summary: 'A service price should cover the hourly person cost before products, overheads, VAT, and profit margin.',
      detail: 'For staff, loaded labour means hourly pay plus employer taxes or payroll loading. For an owner who delivers services, use the owner service hourly target only if you want pricing to pay for that time.',
      category: 'pricing',
      priority: 86,
      actions: ['Add staff hourly pay and hours in Budget.', 'Add employer loading only where it applies.', 'Send the loaded hourly labour cost into Pricing Calculator.']
    }));
  } else {
    cards.push(createCard({
      id: 'pricing-loaded-labour-ready',
      title: 'Pricing Calculator has a labour floor',
      summary: `Budget is sending a loaded labour cost of ${stats.labourHourlyFromBudget.toFixed(2)} per hour into pricing.`,
      detail: 'This is the minimum labour recovery per service hour. The suggested price still needs product cost, overhead, VAT handling, and desired profit margin.',
      category: 'pricing',
      priority: 68,
      actions: ['Check each service uses the correct staff or owner labour profile.', 'Add product usage and overhead before publishing a price.', 'Review VAT-inclusive versus VAT-exclusive display before saving.']
    }));
  }

  if (stats.isIreland && stats.hasStaffPlan) {
    cards.push(createCard({
      id: 'ireland-employer-prsi-loading',
      title: 'Use the right employer PRSI loading for Irish staff',
      summary: prsiSummary,
      detail: 'This loading is an employer cost, not the employee deduction. It belongs in staff labour costing so prices recover the full cost of employing the team.',
      category: 'payroll',
      priority: 92,
      severity: 'warning',
      actions: ['Use the lower rate for Class A weekly pay up to €552.', 'Use the higher rate for Class A weekly pay over €552.', 'Review the percentage again for payrolls on or after 1 October 2026.'],
      sourceIds: ['dspPrsiClassA']
    }));
  }

  if (stats.isIreland && stats.isLimitedCompany && stats.ownerInServiceTeam) {
    cards.push(createCard({
      id: 'ireland-director-prsi-loading',
      title: 'Do not automatically add employer PRSI to owner/director income',
      summary: 'Irish proprietary directors who own or control 50% or more are generally Class S, not Class A employees.',
      detail: 'That usually means no employer PRSI loading for that director income. Directors with less control or a contract of service can be different, so confirm the PRSI class before adding employer loading.',
      category: 'payroll',
      priority: 94,
      severity: 'warning',
      actions: ['Use 0% owner employer loading where the owner is correctly Class S.', 'Use Class A loading only if the director is treated as an employee.', 'Ask an accountant or DSP Scope Section if unsure.'],
      sourceIds: ['dspDirectorPrsi']
    }));
  }

  if (stats.hasMeals && stats.isIreland) {
    cards.push(createCard({
      id: 'meals-tax-warning',
      title: 'Meals and takeaways need extra care',
      summary: 'Food, drink, eating out, and takeaway costs are commonly personal or VAT-blocked.',
      detail: 'VAT on food, meals, eating out, and takeaways is not reclaimable even if the business is VAT registered. Record the gross cost only where it is genuinely business-related.',
      category: 'vat',
      priority: 76,
      severity: 'warning',
      actions: ['Keep notes explaining the business reason.', 'Do not reclaim VAT on food, meals, or takeaways.', 'Record the gross amount only.'],
      sourceIds: ['revenueVatReclaim', 'revenueExpenses']
    }));
  }

  if (stats.hasVehicleFuel && stats.isIreland) {
    cards.push(createCard({
      id: 'fuel-vat-warning',
      title: 'Petrol VAT is usually blocked in Ireland',
      summary: 'Petrol VAT is not reclaimable even if the business is VAT registered.',
      detail: 'Because the app category is Vehicle Fuel, add notes showing whether the receipt is petrol, diesel, or another vehicle cost before VAT filing. Petrol should be recorded gross with VAT reclaim off.',
      category: 'vat',
      priority: 75,
      severity: 'warning',
      actions: ['Mark petrol receipts clearly.', 'Do not reclaim VAT on petrol.', 'Keep mileage/private-use notes for motor costs.'],
      sourceIds: ['revenueVatReclaim']
    }));
  }

  cards.push(createCard({
    id: 'advice-limits',
    title: 'Use this as guidance, not a filing decision',
    summary: 'The AI Assist notes are designed to flag common bookkeeping and tax issues before they become messy.',
    detail: 'Final tax treatment can depend on registration status, invoices, payroll class, mixed-use percentages, and local rules. Confirm uncertain items with an accountant before filing.',
    category: 'support',
    priority: 10,
    actions: ['Keep receipts and notes.', 'Review flagged categories before VAT or tax returns.', 'Use accountant access for final checks.']
  }));

  return cards.sort((a, b) => b.priority - a.priority);
};

const normalizeQuestion = (question) => normalizeString(question)
  .replace(/[^a-z0-9%€$£.\s-]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const formatPlainMoney = (value, currencySymbol = '') => {
  const amount = Number(value) || 0;
  const fixed = amount.toFixed(2);
  return `${currencySymbol || ''}${fixed}`;
};

const questionIncludes = (question, words = []) => (
  words.some((word) => question.includes(word))
);

const findRelevantCardsForQuestion = (question, adviceCards = []) => {
  const tokens = question
    .split(' ')
    .filter((token) => token.length >= 4);

  return adviceCards
    .map((card) => {
      const searchable = `${card.title} ${card.summary} ${card.detail} ${card.category}`.toLowerCase();
      const score = tokens.reduce((sum, token) => sum + (searchable.includes(token) ? 1 : 0), 0);
      return { card, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map((entry) => entry.card);
};

export const buildAiQuestionAnswer = ({
  question = '',
  stats,
  adviceCards = [],
  currentCountry = {},
  referenceDate = new Date()
} = {}) => {
  const cleanQuestion = normalizeQuestion(question);
  const safeStats = stats || buildAiAdvisorStats({ currentCountry, referenceDate });
  const safeCards = Array.isArray(adviceCards) ? adviceCards : [];
  const currencySymbol = currentCountry?.currencySymbol || (safeStats.countryId === 'ie' ? '€' : '');

  if (!cleanQuestion) {
    return {
      title: 'Ask a bookkeeping, tax, VAT, budget, or pricing question',
      answer: 'Type a short question such as "Can I reclaim VAT on food?", "What employer PRSI should I use?", or "How should I price a service with staff?".',
      actions: ['Ask one specific question at a time.', 'Include the category or feature name if relevant.'],
      relatedCards: []
    };
  }

  const prsiSummary = getIrelandEmployerPrsiSummary(referenceDate);
  const isVatQuestion = questionIncludes(cleanQuestion, ['vat', 'reclaim', 'claim back', 'tax back']);
  const isFoodQuestion = questionIncludes(cleanQuestion, ['food', 'meal', 'meals', 'takeaway', 'takeaways', 'eating out', 'restaurant', 'coffee']);
  const isFuelQuestion = questionIncludes(cleanQuestion, ['petrol', 'diesel', 'fuel', 'vehicle']);
  const isVatThresholdQuestion = questionIncludes(cleanQuestion, ['vat threshold', 'register for vat', 'vat registration', 'need to register', 'when register', 'turnover threshold', 'sales threshold']);
  const isVatRateQuestion = questionIncludes(cleanQuestion, ['vat rate', 'rate of vat', 'what vat', 'charge vat', '13.5', '23%', 'beauty vat', 'hair vat', 'retail vat']);
  const isPrsiQuestion = questionIncludes(cleanQuestion, ['prsi', 'employer loading', 'employer tax', 'payroll loading', 'staff loading']);
  const isDirectorQuestion = questionIncludes(cleanQuestion, ['director', 'owner', 'proprietary']);
  const isPricingQuestion = questionIncludes(cleanQuestion, ['price', 'pricing', 'charge', 'service', 'margin']);
  const isBudgetQuestion = questionIncludes(cleanQuestion, ['budget', 'profit goal', 'open hours', 'labour', 'labor', 'staff']);
  const isExpenseQuestion = questionIncludes(cleanQuestion, ['expense', 'claim', 'deduct', 'allowable', 'receipt']);
  const isLoanQuestion = questionIncludes(cleanQuestion, ['loan', 'interest', 'bank charge', 'bank fee']);
  const isCorporationTaxQuestion = questionIncludes(cleanQuestion, ['corporation tax', 'company tax', 'limited company tax']);
  const isRetailQuestion = questionIncludes(cleanQuestion, ['retail', 'product sales', 'stock', 'inventory', 'backbar', 'back bar', 'products used', 'supplies']);
  const isGiftCardQuestion = questionIncludes(cleanQuestion, ['gift card', 'gift voucher', 'voucher', 'deposit', 'prepaid', 'package']);
  const isClothingQuestion = questionIncludes(cleanQuestion, ['clothing', 'clothes', 'uniform', 'workwear', 'apron', 'tunic', 'shoes', 'black clothes', 'dress code']);
  const isTrainingQuestion = questionIncludes(cleanQuestion, ['training', 'course', 'courses', 'education', 'workshop', 'certificate', 'qualification']);
  const isHomeSalonQuestion = questionIncludes(cleanQuestion, ['home salon', 'from home', 'home office', 'house', 'spare room', 'utilities', 'electricity', 'phone', 'mixed use', 'private use']);
  const isTipsQuestion = questionIncludes(cleanQuestion, ['tip', 'tips', 'gratuity', 'gratuities']);
  const isCardFeeQuestion = questionIncludes(cleanQuestion, ['card fee', 'card fees', 'stripe', 'sumup', 'revolut', 'merchant fee', 'payment fee', 'processing fee']);
  const isEquipmentQuestion = questionIncludes(cleanQuestion, ['equipment', 'machine', 'machines', 'furniture', 'laser', 'bed', 'chair', 'capital', 'asset']);
  const isMarketingSoftwareQuestion = questionIncludes(cleanQuestion, ['marketing', 'advertising', 'instagram', 'facebook', 'google ads', 'software', 'subscription', 'booking app']);

  if (safeStats.isIreland && isVatQuestion && isFoodQuestion) {
    return {
      title: 'VAT on food, meals, and takeaways',
      answer: 'No. In Ireland, VAT on food, meals, eating out, and takeaways is not reclaimable regardless of whether the business is VAT registered. Record the gross amount only if it is a genuine business cost.',
      actions: ['Use Meals for food and takeaway receipts.', 'Keep a note explaining the business reason.', 'Do not reclaim VAT on food, meals, or takeaways.'],
      relatedCards: findRelevantCardsForQuestion(cleanQuestion, safeCards)
    };
  }

  if (safeStats.isIreland && isVatQuestion && isFuelQuestion) {
    return {
      title: 'VAT on fuel and vehicle costs',
      answer: 'No VAT should be reclaimed on petrol in Ireland, even when the business is VAT registered. Use notes to separate petrol, diesel, repairs, and other vehicle costs before VAT filing because non-petrol vehicle costs may need a separate review.',
      actions: ['Use Vehicle Fuel for fuel receipts.', 'Add a note saying petrol, diesel, or other fuel.', 'Do not reclaim VAT on petrol.'],
      relatedCards: findRelevantCardsForQuestion(cleanQuestion, safeCards)
    };
  }

  if (safeStats.isIreland && isVatThresholdQuestion) {
    return {
      title: 'Irish VAT registration threshold',
      answer: 'For Ireland, VAT registration is based on turnover, not profit. Revenue lists the main thresholds as €42,500 for services only and €85,000 for goods. A salon with both services and retail products should track the split carefully and review the rolling 12-month turnover before it reaches the threshold.',
      actions: ['Track service sales separately from retail product sales.', 'Review rolling 12-month sales, not just the financial year total.', 'Register or get accountant advice before charging VAT late.'],
      relatedCards: findRelevantCardsForQuestion(cleanQuestion, safeCards)
    };
  }

  if (safeStats.isIreland && isVatRateQuestion) {
    return {
      title: 'VAT rate for Irish beauty businesses',
      answer: safeStats.vatRegistered
        ? 'Revenue lists beauty salon and beauty therapy services at the reduced VAT rate, currently 13.5%. The Revenue beauty salon entry says sales of cosmetics and sunbed treatments are not included and are standard-rated, currently 23%. Check retail products separately before filing.'
        : 'If the business is not VAT registered, do not charge VAT yet. For planning, Revenue lists beauty salon and beauty therapy services at the reduced rate, currently 13.5%, while cosmetics and sunbed treatments are standard-rated, currently 23%.',
      actions: ['Keep service income separate from retail sales.', 'Use 13.5% only for qualifying beauty services.', 'Use Revenue VAT rate search or an accountant for mixed packages.'],
      relatedCards: findRelevantCardsForQuestion(cleanQuestion, safeCards)
    };
  }

  if (isClothingQuestion) {
    return {
      title: 'Clothing, uniforms, and workwear',
      answer: 'Ordinary clothes, shoes, or outfits are not normally a claimable business expense just because they are worn at work. Use Uniform Costs only for salon-only uniforms, branded items, or protective clothing with a clear business reason.',
      actions: ['Do not claim normal clothing used outside work.', 'Keep invoices for branded or protective salon wear.', 'Ask an accountant before claiming dress-code clothing.'],
      relatedCards: findRelevantCardsForQuestion(cleanQuestion, safeCards)
    };
  }

  if (isTrainingQuestion) {
    return {
      title: 'Training and courses',
      answer: 'Training can be a business cost when it supports the services the business already provides or keeps professional skills current. New qualifications, personal development, or mixed-purpose courses need more care because the tax treatment can depend on the facts.',
      actions: ['Use Courses & Learning for relevant training.', 'Keep the invoice and course outline.', 'Add a note explaining which treatment, service, or business activity it supports.'],
      relatedCards: findRelevantCardsForQuestion(cleanQuestion, safeCards)
    };
  }

  if (isGiftCardQuestion) {
    return {
      title: 'Gift cards, vouchers, packages, and deposits',
      answer: 'Track gift cards, vouchers, packages, and booking deposits separately from normal appointment sales. The common mistake is counting the money when the voucher is sold and then counting the full service again when it is redeemed.',
      actions: ['Use Gift Card for voucher sales.', 'When redeemed, record only any extra amount paid by the client.', 'Reverse or adjust deposits when refunded or applied to the appointment.'],
      relatedCards: findRelevantCardsForQuestion(cleanQuestion, safeCards)
    };
  }

  if (isRetailQuestion) {
    return {
      title: 'Retail products, stock, and back-bar supplies',
      answer: 'Separate retail products from treatment-use products. Retail stock is bought to resell to clients. Back-bar or treatment products are consumed while delivering services and should be included in service product costs so prices cover actual usage.',
      actions: ['Post retail product sales to Retail Products income.', 'Post retail stock bought for resale to Retail Products expense.', 'Use Supplies or the product cost calculator for products consumed during treatments.'],
      relatedCards: findRelevantCardsForQuestion(cleanQuestion, safeCards)
    };
  }

  if (isHomeSalonQuestion) {
    return {
      title: 'Home salon and mixed-use costs',
      answer: 'Only the business portion of mixed-use costs should be claimed. For home salon costs, phone, internet, utilities, rent, mortgage-related costs, and vehicle costs need a reasonable business-use calculation and notes explaining the split.',
      actions: ['Record the business-use percentage in the notes.', 'Keep household and vehicle calculations consistent.', 'Do not treat private travel or personal living costs as salon expenses.'],
      relatedCards: findRelevantCardsForQuestion(cleanQuestion, safeCards)
    };
  }

  if (isTipsQuestion) {
    return {
      title: 'Tips and gratuities',
      answer: 'Keep tips separate from service income so sales, margins, and labour cost are not distorted. If tips are passed to staff, keep records showing who received the money and when.',
      actions: ['Use Tips for gratuities.', 'Do not include tips when judging service price profitability.', 'Keep staff tip records separate from normal service sales.'],
      relatedCards: findRelevantCardsForQuestion(cleanQuestion, safeCards)
    };
  }

  if (isCardFeeQuestion) {
    return {
      title: 'Card processing and bank fees',
      answer: 'Card processor fees and bank fees should be recorded as Bank Charges, not hidden inside sales or General Expenses. They are part of the cost of getting paid and should be included in overhead planning.',
      actions: ['Post Stripe, SumUp, Revolut, and merchant fees to Bank Charges.', 'Record gross sales before deducting card fees where possible.', 'Include average payment fees when checking pricing.'],
      relatedCards: findRelevantCardsForQuestion(cleanQuestion, safeCards)
    };
  }

  if (isEquipmentQuestion) {
    return {
      title: 'Salon equipment and larger assets',
      answer: 'Large salon purchases such as machines, furniture, treatment beds, electronics, and vehicles may need capital treatment rather than being treated like day-to-day supplies. Keep them separate so tax and profit reports stay clean.',
      actions: ['Use Equipment, Furniture, Electronics, Vehicle, or Salon Tools.', 'Keep invoices, finance agreements, and warranty documents.', 'Ask an accountant about capital allowances for larger purchases.'],
      relatedCards: findRelevantCardsForQuestion(cleanQuestion, safeCards)
    };
  }

  if (isMarketingSoftwareQuestion) {
    return {
      title: 'Marketing, software, and booking apps',
      answer: 'Marketing costs, booking software, subscriptions, and business apps should usually be tracked as overheads. They should not be added to one treatment unless the cost belongs only to that specific service.',
      actions: ['Use Marketing for ads, flyers, and social campaigns.', 'Use Software/Apps for booking systems and subscriptions.', 'Include recurring costs in the overhead calculator.'],
      relatedCards: findRelevantCardsForQuestion(cleanQuestion, safeCards)
    };
  }

  if (isPrsiQuestion) {
    return {
      title: safeStats.isIreland ? 'Employer PRSI loading' : 'Employer loading',
      answer: safeStats.isIreland
        ? `${prsiSummary} Use this only for employees where Class A employer PRSI applies. It is an employer cost on top of hourly pay.`
        : 'Employer loading should cover employer payroll taxes or mandatory employment costs in your country. Add it only where the business actually pays an employer cost on top of hourly pay.',
      actions: safeStats.isIreland
        ? ['For staff over €552/week, use the higher Class A employer rate.', 'For staff up to €552/week, use the lower Class A employer rate.', 'Do not automatically apply staff PRSI loading to proprietary directors.']
        : ['Ask payroll/accounting support for the correct local employer percentage.', 'Add the percentage in Budget for each staff member.', 'Keep owner pay separate from staff employment costs.'],
      relatedCards: findRelevantCardsForQuestion(cleanQuestion, safeCards)
    };
  }

  if (isDirectorQuestion && safeStats.isLimitedCompany) {
    return {
      title: 'Owner/director pay in a limited company',
      answer: safeStats.isIreland
        ? 'For an Irish limited company, owner/director pay can be payroll, dividends, or director loan movements. Proprietary directors who control 50% or more are generally Class S for PRSI, so do not automatically add employer PRSI to their income. If the owner delivers services, add them as a labour profile only for the hours they actually work.'
        : 'For a limited company, owner pay should be kept separate from normal service profit. Payroll is a company cost, dividends are paid from company profit, and director loan movements are funding or repayments rather than sales.',
      actions: ['Use Staff Costs for payroll.', "Use Director's Loan for owner funding and repayments.", 'Use owner labour in pricing only when the owner delivers services.'],
      relatedCards: findRelevantCardsForQuestion(cleanQuestion, safeCards)
    };
  }

  if (isPricingQuestion || isBudgetQuestion) {
    const labourFloor = safeStats.labourHourlyFromBudget > 0
      ? ` The current labour floor from Budget is ${formatPlainMoney(safeStats.labourHourlyFromBudget, currencySymbol)}/hr.`
      : ' The labour floor has not been set yet.';
    const openHours = safeStats.weeklyOpenHours > 0
      ? ` Weekly open hours are set to ${safeStats.weeklyOpenHours}.`
      : ' Weekly open hours are not set yet.';

    return {
      title: 'Budget and pricing setup',
      answer: `Pricing should recover product cost, loaded labour, overheads, VAT handling, and the business profit goal.${labourFloor}${openHours} The optional margin field is only for extra profit above the Budget goal.`,
      actions: ['Set staff or owner labour in Budget first.', 'Use the actual provider who delivers the service in Pricing Calculator.', 'Check capacity if a service needs too many appointments to meet the weekly goal.'],
      relatedCards: findRelevantCardsForQuestion(cleanQuestion, safeCards)
    };
  }

  if (isCorporationTaxQuestion && safeStats.isLimitedCompany) {
    return {
      title: 'Corporation tax planning',
      answer: safeStats.isIreland
        ? 'For Ireland, the app uses 12.5% corporation tax on trading profits. The Budget profit goal for a limited company is treated as company profit after corporation tax; payroll and dividends are separate owner-pay decisions.'
        : 'For a limited company, corporation tax is based on company profit after allowable company costs. Payroll reduces company profit before corporation tax, while dividends are normally paid from profit after corporation tax.',
      actions: ['Keep payroll costs separate from dividends.', 'Review profit before corporation tax in Tax Calculation.', 'Ask an accountant about passive or non-trading income.'],
      relatedCards: findRelevantCardsForQuestion(cleanQuestion, safeCards)
    };
  }

  if (isLoanQuestion) {
    return {
      title: 'Loans, interest, and bank charges',
      answer: 'Keep loan principal, loan interest, and bank charges separate. Principal repayments are not the same as profit and loss expenses, while business loan interest and bank charges may belong in their own expense categories.',
      actions: ['Use Interest for the finance cost.', 'Use Bank Charges for account and card processing fees.', safeStats.isLimitedCompany ? "Use Director's Loan for owner funding or repayments." : 'Keep personal drawings separate from business loan costs.'],
      relatedCards: findRelevantCardsForQuestion(cleanQuestion, safeCards)
    };
  }

  if (isExpenseQuestion || isVatQuestion) {
    return {
      title: 'Expense and VAT checks',
      answer: safeStats.vatRegistered
        ? 'Use business categories and keep valid invoices. VAT can normally be reclaimed only where the purchase is for taxable business activity and the VAT is not specifically blocked. Do not reclaim VAT on food, meals, takeaways, or petrol.'
        : 'Record genuine business expenses at the gross amount. VAT reclaim should stay off until the business is VAT registered.',
      actions: ['Use specific categories instead of General Expenses where possible.', 'Keep receipts and notes.', 'Record food, meals, takeaways, and petrol gross with no VAT reclaim.'],
      relatedCards: findRelevantCardsForQuestion(cleanQuestion, safeCards)
    };
  }

  const relatedCards = findRelevantCardsForQuestion(cleanQuestion, safeCards);
  if (relatedCards.length > 0) {
    return {
      title: relatedCards[0].title,
      answer: `${relatedCards[0].summary} ${relatedCards[0].detail}`.trim(),
      actions: relatedCards[0].actions || [],
      relatedCards
    };
  }

  return {
    title: 'I can help with that, but I need a little more detail',
    answer: 'Ask about a specific category, VAT treatment, employer loading, director pay, budget setup, or pricing calculation. The answer will use the current business setup and ledger context where possible.',
    actions: ['Try: "Can I reclaim VAT on petrol?"', 'Try: "What employer PRSI should I use?"', 'Try: "How should I price a staff service?"'],
    relatedCards: []
  };
};

export default buildAiAdviceCards;
